import { useState, useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Play } from "lucide-react";
import SEO from "@/components/SEO";

const V = `${process.env.PUBLIC_URL}/videos`;

const VIDEOS = [
  {
    id: "onboarding",
    title: "How to Onboard",
    description: "Get started with NeuroNest — choose your mode and set up your space.",
    youtube: "https://www.youtube-nocookie.com/embed/h4ZZ6q8Djf8",
    src: `${V}/onboarding.mp4`,
    category: "Getting Started",
  },
  {
    id: "settings",
    title: "Settings & Profile",
    description: "Customise your experience — sensory profile, notifications, accessibility, and more.",
    src: `${V}/settings.mp4`,
    category: "Getting Started",
  },
  {
    id: "individual-mode",
    title: "Individual Mode",
    description: "Full access to every feature, designed around how your brain works.",
    youtube: "https://www.youtube-nocookie.com/embed/4PVf37KV0bA",
    src: `${V}/individual-mode.mp4`,
    category: "Modes",
  },
  {
    id: "parent-mode",
    title: "Parent Mode",
    description: "Stay connected to your child's experience with dashboards and notifications.",
    youtube: "https://www.youtube-nocookie.com/embed/JTd1Q7A2qZo",
    src: `${V}/parent-mode.mp4`,
    category: "Modes",
  },
  {
    id: "check-ins",
    title: "Check-Ins",
    description: "Quick mood and energy snapshots — no streaks, no guilt.",
    src: `${V}/check-ins.mp4`,
    category: "Core Tools",
  },
  {
    id: "routines",
    title: "Routines",
    description: "Build visual, step-by-step routines for your day with the Routine Player.",
    youtube: "https://www.youtube-nocookie.com/embed/yzNkdqH3CNo",
    src: `${V}/routines.mp4`,
    category: "Core Tools",
  },
  {
    id: "transitions",
    title: "Transitions",
    description: "Step-by-step guides for tricky moments like leaving the house or bedtime.",
    youtube: "https://www.youtube-nocookie.com/embed/vKvOrjik6iQ",
    src: `${V}/transitions.mp4`,
    category: "Core Tools",
  },
  {
    id: "circuits",
    title: "Circuits",
    description: "Guided sensory circuits to help you regulate and reset.",
    src: `${V}/circuits.mp4`,
    category: "Core Tools",
  },
  {
    id: "community-places",
    title: "Community Places",
    description: "Discover over 1,370 sensory-friendly locations across the UK.",
    youtube: "https://www.youtube-nocookie.com/embed/5SzlBI50kPA",
    src: `${V}/community-places.mp4`,
    category: "Core Tools",
  },
  {
    id: "fidget-tools",
    title: "Fidget Tools",
    description: "Four sensory-safe fidget experiences — Bubble Pop, Ripple Pond, Feather Drift, and Fidget Spinner.",
    src: `${V}/fidget-tools.mp4`,
    category: "Core Tools",
  },
  {
    id: "my-space",
    title: "My Space",
    description: "Your personalised comfort hub — anchors, fidgets, and sensory mood.",
    src: `${V}/my-space.mp4`,
    category: "Core Tools",
  },
  {
    id: "insights",
    title: "Insights",
    description: "Gentle emotional patterns and personalised recommendations.",
    src: `${V}/insights.mp4`,
    category: "Core Tools",
  },
];

const CATEGORIES = [...new Set(VIDEOS.map((v) => v.category))];

export default function VideosPage() {
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="videos-page" className="min-h-screen bg-[#F8F9FB]">
      <SEO
        title="Video Guides"
        description="Short video walkthroughs of NeuroNest's features — check-ins, routines, fidget tools, community places, insights, and all four app modes."
        path="/videos"
      />
      <Navbar />

      <main className="pt-28 pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-brand-teal font-semibold mb-4">
              See it in action
            </p>
            <h1
              data-testid="videos-title"
              className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight mb-4"
            >
              Video Guides
            </h1>
            <p className="text-base md:text-lg text-[#5A6376] max-w-2xl mx-auto leading-relaxed">
              Short walkthroughs of NeuroNest's features and modes. Tap a card to watch.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Phone Mockup Player */}
            <div id="video-player-area" className="flex flex-col items-center lg:sticky lg:top-28 order-1 lg:order-2">
              <div className="relative w-[280px] md:w-[300px]">
                <div className="relative bg-[#2F3A56] rounded-[40px] p-[10px] shadow-[0_20px_60px_rgba(47,58,86,0.25)]">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[100px] h-[22px] bg-[#2F3A56] rounded-b-2xl z-10" />
                  {/* Screen */}
                  <div className="relative rounded-[30px] overflow-hidden bg-[#1A1D24] aspect-[9/16]">
                    {activeVideo ? (
                      activeVideo.src ? (
                        <video
                          data-testid="video-player"
                          key={activeVideo.id}
                          src={activeVideo.src}
                          className="w-full h-full object-cover block"
                          controls
                          autoPlay
                          playsInline
                        />
                      ) : (
                        <iframe
                          data-testid="video-player"
                          key={activeVideo.id}
                          src={`${activeVideo.youtube}?autoplay=1&rel=0&modestbranding=1`}
                          title={activeVideo.title}
                          className="w-full h-full block"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                          allowFullScreen
                          frameBorder="0"
                        />
                      )
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-center px-8">
                        <div className="w-16 h-16 rounded-full bg-brand-teal/20 flex items-center justify-center mb-4">
                          <Play size={28} className="text-brand-teal ml-1" strokeWidth={1.5} />
                        </div>
                        <p className="text-white/60 text-sm font-medium">
                          Select a video to watch
                        </p>
                      </div>
                    )}
                  </div>
                  {/* Home indicator */}
                  <div className="absolute bottom-[6px] left-1/2 -translate-x-1/2 w-[100px] h-[4px] bg-white/30 rounded-full" />
                </div>
              </div>
              {activeVideo && (
                <div className="text-center mt-6 max-w-[300px]">
                  <p className="font-heading text-base font-semibold text-[#2F3A56]">
                    {activeVideo.title}
                  </p>
                  <p className="text-xs text-[#8B95A5] mt-1">
                    {activeVideo.description}
                  </p>
                </div>
              )}
            </div>

            {/* Video Cards */}
            <div className="order-2 lg:order-1">
              {CATEGORIES.map((category) => (
                <div key={category} className="mb-8">
                  <h2 className="font-heading text-sm uppercase tracking-widest text-[#8B95A5] font-semibold mb-4">
                    {category}
                  </h2>
                  <div className="space-y-3">
                    {VIDEOS.filter((v) => v.category === category).map((video) => {
                      const isActive = activeVideo?.id === video.id;
                      return (
                        <button
                          key={video.id}
                          data-testid={`video-card-${video.id}`}
                          onClick={() => {
                            setActiveVideo(video);
                            document.getElementById("video-player-area")?.scrollIntoView({ behavior: "smooth", block: "center" });
                          }}
                          className={`w-full text-left flex items-center gap-4 p-4 rounded-2xl border transition-all duration-300 ${
                            isActive
                              ? "bg-brand-teal/5 border-brand-teal/30 shadow-[0_4px_20px_rgba(110,198,193,0.12)]"
                              : "bg-white border-[#E7E9EC] hover:border-brand-teal/20 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)]"
                          }`}
                        >
                          <div
                            className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                              isActive ? "bg-brand-teal" : "bg-[#F0F1F3]"
                            }`}
                          >
                            <Play
                              size={18}
                              className={isActive ? "text-white ml-0.5" : "text-[#8B95A5] ml-0.5"}
                              strokeWidth={1.5}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-sm font-semibold leading-snug ${
                                isActive ? "text-brand-teal" : "text-[#2F3A56]"
                              }`}
                            >
                              {video.title}
                            </p>
                            <p className="text-xs text-[#8B95A5] mt-0.5 truncate">
                              {video.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="mt-8 p-5 bg-white rounded-2xl border border-[#E7E9EC]">
                <p className="text-xs text-[#8B95A5] leading-relaxed">
                  More videos coming soon — including Check-Ins, Routines,
                  Community Places, Fidget Tools, Child Mode, Carer Mode, and more.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
